import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PositionsList, WebserviceResponse } from '../../../../../_shared/interfaces';
import { AlertService, IAlertType, ProjectsStoreService } from '../../../../../_shared/services';
import { JsonPipe, NgIf } from '@angular/common';
import { ClrCommonFormsModule, ClrIconModule } from "@clr/angular";
import { ClarityIcons, floppyIcon } from "@cds/core/icon";
import { environment as env } from "../../../../../../environments/environment";

// API Model for position update
interface PositionUpdateModel {
  comment_phm: string;
  controlling_nr: string;
  description: string;
  heat_nr: string;
  material: string;
  name: string;
  number_of_pieces: number;
  position: number;
  position_part: number;
  project_number: string;
  serial_nr: string;
  specification: string;
  status: string;
  taric: string;
  technical_drawing: string;
  technology: boolean;
  weight: string;
}

@Component({
  selector: 'app-edit-position',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    ClrIconModule,
    JsonPipe,
    ClrCommonFormsModule
  ],
  templateUrl: './edit-position.component.html',
  styleUrl: './edit-position.component.sass'
})
export class EditPositionComponent implements OnInit {
  public editPositionsForm: FormGroup;
  public positions: PositionsList[] = [];
  public isLoading = false;
  public hasError = false;
  public devMode: boolean;
  public positionData: PositionUpdateModel | null = null;

  @Input() projectNumber: string = '';
  @Input() editPosition: number = 0;
  @Input() editPart: number = 0;
  @Output() updatedPosition = new EventEmitter<boolean>();

  constructor(
    private prss: ProjectsStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
  ) {
    ClarityIcons.addIcons(floppyIcon);
    this.editPositionsForm = this.createForm();
    this.devMode = env.stages.dev;
  }

  ngOnInit() {
    if (!this.projectNumber || this.editPosition === undefined) {
      this.alertDispatcher.show({
        message: 'Fehlende Projekt- oder Positionsdaten',
        clrAlertType: IAlertType.DANGER
      }, 2000);
      return;
    }
    this.loadProjectsPosition(this.projectNumber, this.editPosition, this.editPart);
  }

  private createForm(position?: PositionsList): FormGroup {
    return this.fb.group({
      comment_phm: [position?.comment_phm || ''],
      controlling_nr: [position?.controlling_nr || ''],
      description: [position?.description || ''],
      heat_nr: [position?.heat_nr || ''],
      material: [position?.material || ''],
      name: [position?.name || ''],
      number_of_pieces: [position?.number_of_pieces ?? 0],
      position: [this.editPosition],
      position_part: [this.editPart],
      project_number: [this.projectNumber],
      serial_nr: [position?.serial_nr || ''],
      specification: [position?.specification || ''],
      status: [position?.status || ''],
      taric: [position?.taric ?? '0'],
      technical_drawing: [position?.technical_drawing || ''],
      technology: [position?.technology ?? false],
      weight: [position?.weight || '']
    });
  }

  loadProjectsPosition(prId: string, posId: number, partId: number) {
    this.isLoading = true;
    this.hasError = false;
    const posID = posId.toString();
    const partID = partId.toString();

    this.prss.getPositionsParts(prId, posID, partID).subscribe({
      next: (data) => {
        if (!data?.object || !Array.isArray(data.object) || data.object.length === 0) {
          this.alertDispatcher.show({
            message: 'Keine Komponenten gefunden!',
            clrAlertType: IAlertType.WARNING
          }, 2000);
          this.hasError = true;
          return;
        }
        // Ensure all required fields are present and properly typed
        this.positions = data.object.map(pos => ({
          ...pos,
          number_of_pieces: pos.number_of_pieces ?? 0,
          position: pos.position ?? posId,
          positions_part: pos.positions_part ?? partId,
          project_number: pos.project_number ?? this.projectNumber,
          technical_drawing: pos.technical_drawing || '',
          name: pos.name || '',
          description: pos.description || '',
          material: pos.material || '',
          serial_nr: pos.serial_nr || '',
          heat_nr: pos.heat_nr || '',
          controlling_nr: pos.controlling_nr || '',
          status: pos.status || '',
          specification: pos.specification || '',
          comment_phm: pos.comment_phm || '',
          taric: pos.taric ?? '0',
          technology: pos.technology ?? false,
          weight: pos.weight || ''
        }));
        if (this.devMode) console.log('Loaded positions:', this.positions);

        if (this.positions[0]) {
          this.editPositionsForm.patchValue({
            comment_phm: this.positions[0].comment_phm || '',
            controlling_nr: this.positions[0].controlling_nr || '',
            description: this.positions[0].description || '',
            heat_nr: this.positions[0].heat_nr || '',
            material: this.positions[0].material || '',
            name: this.positions[0].name || '',
            number_of_pieces: this.positions[0].number_of_pieces ?? 0,
            position: this.editPosition,
            position_part: this.editPart,
            project_number: this.projectNumber,
            serial_nr: this.positions[0].serial_nr || '',
            specification: this.positions[0].specification || '',
            status: this.positions[0].status || '',
            taric: this.positions[0].taric ?? '0',
            technical_drawing: this.positions[0].technical_drawing || '',
            technology: this.positions[0].technology ?? false,
            weight: this.positions[0].weight || ''
          });
          if (this.devMode) console.log('Form value after patch:', this.editPositionsForm.value);
        }
      },
      error: (error) => {
        console.error('Error loading position:', error);
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Position',
          clrAlertType: IAlertType.DANGER
        }, 2000);
        this.hasError = true;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  updatePosition() {
    if (this.editPositionsForm.invalid) {
      this.alertDispatcher.show({
        message: 'Bitte füllen Sie alle erforderlichen Felder aus',
        clrAlertType: IAlertType.WARNING
      }, 2000);
      return;
    }

    const formValue = this.editPositionsForm.value;
    this.positionData = {
      comment_phm: formValue.comment_phm || '',
      controlling_nr: formValue.controlling_nr || '',
      description: formValue.description || '',
      heat_nr: formValue.heat_nr || '',
      material: formValue.material || '',
      name: formValue.name || '',
      number_of_pieces: formValue.number_of_pieces ?? 0,
      position: this.editPosition,
      position_part: this.editPart,
      project_number: this.projectNumber,
      serial_nr: formValue.serial_nr || '',
      specification: formValue.specification || '',
      status: formValue.status || '',
      taric: formValue.taric ?? '0',
      technical_drawing: formValue.technical_drawing || '',
      technology: formValue.technology ?? false,
      weight: formValue.weight || ''
    };

    this.isLoading = true;
    this.prss.updatePosition(this.positionData).subscribe({
      next: (data: WebserviceResponse<PositionsList>) => {
        if (data?.hasErrors === false) {
          this.alertDispatcher.show({
            message: 'Position erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 2000);
          this.updatedPosition.emit(true);
        } else {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren der Position',
            clrAlertType: IAlertType.DANGER
          }, 2000);
        }
      },
      error: (error) => {
        console.error('Error updating position:', error);
        this.alertDispatcher.show({
          message: 'Fehler beim Aktualisieren der Position',
          clrAlertType: IAlertType.DANGER
        }, 2000);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  cancelEdit() {
    this.updatedPosition.emit(true);
  }
}
